import axios from "../utils/axios";
import toast from "react-hot-toast";
import { useMutation, useQueryClient, useQuery } from "react-query";
import { ERROR_MESSAGES } from "../utils/constants";
import { checkAuthTokenFn, removeUserCookies } from "../utils/cookieHelper";
import { useNavigate } from "react-router-dom";


const getAllClassroom = async () => {
    checkAuthTokenFn();
    const response = await axios.get(`/classroom`);
    return response.data;
};

export const useGetAllClassroom = () => {
    const navigate = useNavigate();
    const { data, isLoading } = useQuery(['/classrooms'], getAllClassroom, {
        retry: false,
        onError: (error) => {
            if (error?.code === 'ERR_NETWORK' || error?.code === "ERR_BAD_RESPONSE") {
                toast.error(ERROR_MESSAGES["ERR_NETWORK"]);
                return;
            }
            if (error?.response?.data?.code === 401 || error?.response?.data?.code === 403) {
                removeUserCookies();
                navigate('/login');
                return;
            }
            toast.error(ERROR_MESSAGES[error?.response?.data?.code], { duration: 7000 });
        },
        staleTime: 24 * 60 * 60 * 1000,
    });

    return {
        response: data,
        isLoading,
    };
};

const getClassroomById = async (id) => {
    checkAuthTokenFn();
    const response = await axios.get(`/classroom/${id}`);
    return response.data;
};

export const useGetClassroomById = (id) => {
    const navigate = useNavigate();

    const { data, isLoading } = useQuery(['/classrooms', id], () => getClassroomById(id), {
        retry: false,
        onError: (error) => {
            if (error?.code === 'ERR_NETWORK' || error?.code === "ERR_BAD_RESPONSE") {
                toast.error(ERROR_MESSAGES["ERR_NETWORK"]);
                return;
            }

            if (
                (error.response.data.message.includes(`Classroom with ID ${id} does not exist`) && error.response.data.code === 404)
                ||
                (error.response.data.message.includes(`Value of ID ${id} is incorrect ObjectId`) && error.response.data.code === 400)
            ) {
                toast(ERROR_MESSAGES["ERR_ID_CLASSROOM"], {
                    icon: '⚠️',
                    duration: 5000,
                });
                navigate('/dashboard?tab=classroom');
                return;
            }

            if (error?.response?.data?.code === 401 || error?.response?.data?.code === 403) {
                removeUserCookies();
                navigate('/login');
                return;
            }

            toast.error(ERROR_MESSAGES[error.response.data.code], { duration: 7000 });
        },
        staleTime: 24 * 60 * 60 * 1000,
    });

    return {
        response: data,
        isLoading,
    };
};

const createClassroom = async (content) => {
    checkAuthTokenFn();
    const resp = await axios.post("/classroom", content);
    return resp.data;
};

export const useCreateClassroom = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { mutateAsync, isLoading } = useMutation(createClassroom, {
        retry: false,
        onSuccess: () => {
            queryClient.invalidateQueries('/classrooms');
            toast.success("La tua nuova classe è stata creata");
        },
        onError: async (error) => {
            if (error?.code === 'ERR_NETWORK' || error?.code === "ERR_BAD_RESPONSE") {
                toast.error(ERROR_MESSAGES["ERR_NETWORK"]);
                return;
            }
            if (error?.response?.data?.message === 'You have reached the maximum limit of classrooms') {
                toast(ERROR_MESSAGES["ERR_REACHED_MAX_LIMIT_OF_CLASSROOM"], {
                    icon: '⚠️',
                    duration: 5000,
                });
                navigate('/dashboard?tab=classroom');
                return;
            }
            if (error?.response?.data?.message === 'Classroom already exists') {
                toast(ERROR_MESSAGES["ERR_EXIST_CLASSROOM"], {
                    icon: '⚠️',
                    duration: 5000,
                });
                return;
            }

            if (error?.response?.data?.code === 401 || error?.response?.data?.code === 403) {
                removeUserCookies();
                navigate('/login');
                return;
            }
            toast.error(ERROR_MESSAGES[error.response.data.code], { duration: 7000 });
        },
    });
    return {
        createClassroom: mutateAsync,
        isCreating: isLoading,
    };
};


const changeClassroom = async ({ id, name }) => {
    checkAuthTokenFn();
    const resp = await axios.patch(`/classroom/${id}`, { name });
    return resp.data;
};

export const useChangeClassroom = () => {
    const queryClient = useQueryClient();
    const { mutateAsync, isLoading } = useMutation(changeClassroom, {
        retry: false,
        onSuccess: () => {
            queryClient.invalidateQueries('/classrooms');
            toast.success("La modifica è avvenuta con successo");
        },
        onError: async (error) => {
            if (error?.code === 'ERR_NETWORK' || error?.code === "ERR_BAD_RESPONSE") {
                toast.error(ERROR_MESSAGES["ERR_NETWORK"]);
                return;
            }
            if (error?.response?.data?.code === 401 || error?.response?.data?.code === 403) {
                removeUserCookies();
                navigate('/login');
                return;
            }
            if (error?.response?.data?.message === 'Classroom already exists') {
                toast(ERROR_MESSAGES["ERR_EXIST_CLASSROOM"], {
                    icon: '⚠️',
                    duration: 5000,
                });
                return;
            }
            toast.error(ERROR_MESSAGES[error.response.data.code], { duration: 7000 });
        },
    });
    return {
        changeClassroom: mutateAsync,
        isChanging: isLoading,
    };
};

const deleteClassroom = async id => {
    checkAuthTokenFn();
    const resp = await axios.delete(`/classroom/${id}`);
    return resp.data;
};

export const useDeleteClassroomById = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { mutateAsync, isLoading } = useMutation(deleteClassroom, {
        retry: false,
        onSuccess: () => {
            queryClient.invalidateQueries('/classrooms');
            toast.success('La classroom e stata cancellata');
        },
        onError: async (error) => {
            if (error?.code === 'ERR_NETWORK' || error?.code === "ERR_BAD_RESPONSE") {
                toast.error(ERROR_MESSAGES["ERR_NETWORK"]);
                return;
            }
            if (error?.response?.data?.code === 401 || error?.response?.data?.code === 403) {
                removeUserCookies();
                navigate('/login');
                return;
            }
            toast.error(ERROR_MESSAGES[error?.response?.data?.code], { duration: 7000 });
        },
    });

    return {
        deleteClassroom: mutateAsync,
        isDeleting: isLoading,
    };
};

